import React, { forwardRef } from "react";
import type { FC, JSX, PropsWithChildren, MouseEventHandler } from "react";

import cn from "classnames";

import { ButtonCore } from "../../helpers/common/ButtonCore";
import { ButtonDiv } from "../../helpers/common/ButtonDiv";
import { useMouseDownAnimation } from "../../hooks/useMouseDownAnimation";
import { getChildrenRecord } from "../../utils/react";

import type { IUniCell, UniCellProps } from "./types";

import classes from "./UniCell.styl";

export const UNI_CELL_WRAPPER_TEST_ID = "unicell-wrapper-test-id";
const UNI_CELL_TEST_ID = "unicell-test-id";
const UNI_CELL_CONTENT_TEST_ID = "unicell-content-test-id";
const UNI_CELL_DISPLAY_NAME = "UniCell";

interface IUniCellContent
    extends PropsWithChildren,
        Pick<IUniCell, "additionalContentAlignSelf"> {}

const UniCellContent: FC<IUniCellContent> = ({
    children,
    additionalContentAlignSelf = "center",
}) => {
    const { Left, Middle, Right, Bottom, Buttons } = getChildrenRecord(
        children,
        "UniCell",
        {
            Middle: {
                required: true,
            },
            Unknown: {
                skipElement: true,
            },
        },
    );

    return (
        <>
            {Left}
            {/* eslint-disable-next-line ok-custom-rules/check-attribute */}
            <div
                className={cn(
                    classes["additional-content"],
                    classes[`__align-self-${additionalContentAlignSelf}`],
                )}
                data-tsid={UNI_CELL_CONTENT_TEST_ID}
            >
                <div className={classes["middle-right-content"]}>
                    {Middle}
                    {Right}
                </div>
                {Bottom}
                {Buttons}
            </div>
        </>
    );
};

UniCellContent.displayName = "UniCellContent";

/**
 * Контейнер, который может принимать в себя множество дочерних компонентов и формировать из них различные композиции в виде ячейки.
 *
 * На стороне дизайна служит конструктором, в который можно положить как готовые предустановленные компоненты, так и локальные, не имеющие готовой интерпретации в ДС.
 *
 * Систематизирует динамические отступы, обрабатывает состояния
 *
 * <a href="https://www.figma.com/design/64y1aDfl0lSSpek3sq1Lrt/%F0%9F%8D%AC-Cards-%26-Cells-%26-Views?node-id=2733-14217&node-type=frame&m=dev" target="_blank">Спека</a>
 */
export const UniCell = forwardRef<HTMLDivElement, UniCellProps>(
    (
        {
            customColorsStyles,
            view = "primary",
            state = "none",
            customVarsStyles,
            children,
            "data-tsid": tsid,
            draggable = false,
            disableRightAddonOffset = false,
            onMouseDown,
            additionalContentAlignSelf,
            ...props
        },
        ref,
    ): JSX.Element => {
        const { clicked, mousedownHandler } = useMouseDownAnimation();

        const handleMouseDownEvent: MouseEventHandler<HTMLDivElement> = (e) => {
            onMouseDown?.(e);
            mousedownHandler();
        };

        const wrapperClasses = cn(
            classes.wrapper,
            {
                [classes["__view-custom"]]: view === "custom",
                [classes["__disable-right-addon-offset"]]:
                    disableRightAddonOffset,
            },
            customVarsStyles,
            customColorsStyles,
        );

        const containerClasses = cn(
            classes["container"],
            state !== "none" && classes[`__state-${state}`],
            {
                [classes["__clicked"]]: clicked,
                [classes["__disable-animation"]]: draggable,
                [classes["__disable-right-addon-offset"]]:
                    disableRightAddonOffset,
            },
        );

        const dataTestId = tsid || UNI_CELL_TEST_ID;

        if (state === "none") {
            return (
                <div className={wrapperClasses}>
                    <div className={containerClasses}>
                        <div
                            className={classes.cell}
                            data-tsid={dataTestId}
                            data-uikit={UNI_CELL_DISPLAY_NAME}
                        >
                            <UniCellContent
                                additionalContentAlignSelf={
                                    additionalContentAlignSelf
                                }
                            >
                                {children}
                            </UniCellContent>
                        </div>
                    </div>
                </div>
            );
        }

        const stateForButton = state === "selected" ? undefined : state;

        return (
            <div
                className={wrapperClasses}
                data-tsid={UNI_CELL_WRAPPER_TEST_ID}
            >
                <ButtonCore
                    as={ButtonDiv}
                    data-uikit={UNI_CELL_DISPLAY_NAME}
                    {...props}
                    className={classes.cell}
                    wrapperClassName={containerClasses}
                    onMouseDown={handleMouseDownEvent}
                    state={stateForButton}
                    data-tsid={dataTestId}
                    disableAnimation={draggable}
                    ref={ref}
                >
                    <UniCellContent
                        additionalContentAlignSelf={additionalContentAlignSelf}
                    >
                        {children}
                    </UniCellContent>
                </ButtonCore>
            </div>
        );
    },
);

UniCell.displayName = UNI_CELL_DISPLAY_NAME;

export default UniCell;
